import React, { useEffect, useState } from "react";
import useEddProgress from "../../hooks/useEddProgress";
import Footer from "../Footer/Footer";
import FormSection from "../FormSection/FormSection";
import Header from "../Header/Header";
import ErrorState from "../ui/ErrorState/ErrorState";
import Loader from "../ui/Loader/Loader";

interface EddFormProps {
  onSubmissionSuccess: () => void;
  isHmac: boolean;
}

/** Progress doc requirement → every required doc must be in uploadedDocuments. */
const docsSatisfied = (sections: any[] | undefined, uploaded: any[]) => {
  const docList = sections?.find((s: any) => s?.tag === "DOCUMENT")?.documents;
  return (
    !docList?.length ||
    docList.every(
      (d: any) =>
        d.optional ||
        d.status === "COMPLETED" ||
        uploaded.some((u: any) => u.document_tag === d.tag)
    )
  );
};

const EddForm: React.FC<EddFormProps> = ({ onSubmissionSuccess, isHmac }) => {
  const [openAccordionId, setOpenAccordionId] = useState<number>(0);
  const [enabledSectionsCount, setEnabledSectionsCount] = useState<number>(0);
  const [enableSubmitButton, setEnableSubmitButton] = useState<boolean>(false);
  const [isSendingEddData, setIsSendingEddData] = useState<boolean>(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);
  const [isSubmittingEddData, setIsSubmittingEddData] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    data: eddFormData,
    refresh: refreshEddFormData,
    isFetching: isFetchingEddFormData
  } = useEddProgress();

  const sections = eddFormData?.sections;
  const canSubmit =
    enableSubmitButton && docsSatisfied(sections, uploadedDocuments);

  const handleError = (message: string) => {
    if (isHmac) {
      setErrorMessage(message);
    }
  };

  useEffect(() => {
    const sections = eddFormData?.sections;
    if (!sections) return;

    const firstPendingSectionId = sections.findIndex(
      (section: any) => section?.status === "PENDING"
    );
    if (!isNaN(firstPendingSectionId) && firstPendingSectionId >= 0) {
      setEnabledSectionsCount(prev =>
        Math.max(firstPendingSectionId + 1, prev)
      );
      setOpenAccordionId(firstPendingSectionId);
    }
  }, [eddFormData?.sections]);

  if (isHmac && errorMessage) {
    return (
      <div className="p-4 max-w-md mx-auto">
        <Header isSubmittingEddData={false} />
        <div className="mt-14">
          <ErrorState
            icon="Warning"
            iconColor="#EF4444"
            title="Technical Error"
            description={
              errorMessage || "Please contact support if the issue persists."
            }
            fullScreen={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-md mx-auto">
      <Header isSubmittingEddData={isSubmittingEddData} />
      <div className="mt-14 mb-40">
        {isFetchingEddFormData ? (
          <div className="flex items-center justify-center h-[75vh]">
            <Loader color="purple" />
          </div>
        ) : (
          eddFormData?.sections?.map((section: any, index: number) => (
            <FormSection
              key={section.tag || index}
              section={section}
              openAccordionId={openAccordionId}
              setOpenAccordionId={setOpenAccordionId}
              id={index}
              enabledSectionsCount={enabledSectionsCount}
              setEnabledSectionsCount={setEnabledSectionsCount}
              refreshEddFormData={refreshEddFormData}
              setEnableSubmitButton={setEnableSubmitButton}
              isSendingEddData={isSendingEddData}
              setIsSendingEddData={setIsSendingEddData}
              uploadedDocuments={uploadedDocuments}
              setUploadedDocuments={setUploadedDocuments}
            />
          ))
        )}
      </div>
      <Footer
        enableSubmitButton={canSubmit}
        uploadedDocuments={uploadedDocuments}
        setUploadedDocuments={setUploadedDocuments}
        enabledSectionsCount={enabledSectionsCount}
        isSendingEddData={isSendingEddData}
        formProgress={eddFormData?.progress ?? 0}
        isSubmittingEddData={isSubmittingEddData}
        setIsSubmittingEddData={setIsSubmittingEddData}
        onSubmissionSuccess={onSubmissionSuccess}
        onError={handleError}
      />
    </div>
  );
};

export default EddForm;
